import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";

import { Card, CardDescription, CardHeader, CardTitle } from "@/client/components/ui/card";
import { Skeleton } from "@/client/components/ui/skeleton";
import { cn } from "@/client/lib/utils";

/**
 * A compact, clickable metric tile for the portal landing pages. `value` is
 * `null` while the underlying data is still loading (renders a skeleton), so the
 * card never flashes a misleading zero. The whole tile links to the section the
 * metric belongs to.
 */
export function StatCard({
  to,
  label,
  value,
  hint,
  icon: Icon,
}: {
  to: string;
  label: string;
  value: number | null;
  hint: string;
  icon?: LucideIcon;
}) {
  return (
    <Link to={to} className="group rounded-xl">
      <Card className="group-hover:border-foreground/20 group-hover:bg-accent/30 h-full transition-colors">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardDescription>{label}</CardDescription>
            {Icon ? <Icon className="text-muted-foreground size-4" /> : null}
          </div>
          {value === null ? (
            <Skeleton className="mt-1 h-9 w-12" />
          ) : (
            <CardTitle className={cn("text-3xl tabular-nums")}>{value}</CardTitle>
          )}
          <p className="text-muted-foreground text-xs">{hint}</p>
        </CardHeader>
      </Card>
    </Link>
  );
}
